/**
 * The candidate declarations — Right to Represent (optional Yes / No) and the Fraud Declaration
 * (mandatory; only Yes is accepted). One component so the wording can't drift between the
 * places that collect them: the profile page, the invite claim form (recruitment workflow §6.3)
 * and the declaration popup on apply (§6.4, step 5e).
 *
 * Wording moved verbatim from ProfileFormSections in step 5c. Stateless: the parent owns the
 * answers and decides what to do with a missing fraud declaration.
 */

export type DeclarationAnswer = '' | 'yes' | 'no';

interface Props {
  rtr: DeclarationAnswer;
  fraud: DeclarationAnswer;
  onRtrChange: (value: 'yes' | 'no') => void;
  onFraudChange: (value: 'yes' | 'no') => void;
  /** Read-only "last updated" captions under each group; omitted when null / undefined. */
  rtrConsentAt?: string | null;
  fraudConsentAt?: string | null;
  /** DOM id on the fraud block so a parent can scroll to it when it's missing. */
  fraudWrapperId?: string;
  /** Inline error under the fraud block. */
  fraudError?: string | null;
  /** Prefix for the radio `name`s, so two instances on one page don't share a group. */
  namePrefix?: string;
  disabled?: boolean;
}

function formatConsentDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function DeclarationFields({
  rtr,
  fraud,
  onRtrChange,
  onFraudChange,
  rtrConsentAt,
  fraudConsentAt,
  fraudWrapperId,
  fraudError,
  namePrefix = '',
  disabled,
}: Props) {
  return (
    <div className="space-y-6">
      {/* Right to Represent — optional Yes/No. */}
      <div>
        <p className="text-sm text-brand-primary mb-3">
          I agree to give QuantumHire the right to represent my candidature
          to their clients. This offer is exclusive for a period of 30 days
          from today. I declare that all information submitted in my profile
          is true, complete, and accurate to the best of my knowledge, and
          that I will represent my background, qualifications, and experience
          factually and honestly.
        </p>
        <YesNo name={`${namePrefix}rtr_consent`} value={rtr} onChange={onRtrChange} disabled={disabled} />
        {formatConsentDate(rtrConsentAt) && (
          <p className="text-xs text-brand-muted mt-2">
            Last updated: {formatConsentDate(rtrConsentAt)}
          </p>
        )}
      </div>

      {/* Fraud declaration — mandatory; Yes required. */}
      <div id={fraudWrapperId}>
        <p className="text-sm text-brand-primary mb-3">
          I agree not to resort to any fraudulent means to gain unfair advantage
          or mislead the interviewer about my abilities, knowledge, experience
          and background. I will represent my background factually, to the best
          of my knowledge, not use proxies or aids during evaluation by
          QuantumHire or its customers or use any means or tools which materially
          impact the judgement of the interviewer of my abilities, knowledge,
          experience or skill. I will produce authentic documents regarding
          experience, academics, certifications and other artefacts as needed
          to evaluate my suitability for the role I am being considered. I hereby
          commit that I will undergo with process with all authenticity, integrity,
          interest and commitment. <span className="text-red-700">*</span>
        </p>
        <YesNo name={`${namePrefix}fraud_declaration`} value={fraud} onChange={onFraudChange} disabled={disabled} />
        {formatConsentDate(fraudConsentAt) && (
          <p className="text-xs text-brand-muted mt-2">
            Last consented: {formatConsentDate(fraudConsentAt)}
          </p>
        )}
        {fraudError && <p className="text-xs text-red-700 font-medium mt-2">{fraudError}</p>}
      </div>
    </div>
  );
}

function YesNo({
  name,
  value,
  onChange,
  disabled,
}: {
  name: string;
  value: DeclarationAnswer;
  onChange: (value: 'yes' | 'no') => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex gap-6">
      {(['yes', 'no'] as const).map((option) => (
        <label key={option} className="inline-flex items-center gap-2 cursor-pointer text-sm text-brand-primary">
          <input
            type="radio"
            name={name}
            value={option}
            checked={value === option}
            onChange={() => onChange(option)}
            disabled={disabled}
            className="accent-brand-primary"
          />
          {option === 'yes' ? 'Yes' : 'No'}
        </label>
      ))}
    </div>
  );
}
